// /tours/$id — route detail with live match preview + planning controls.
//
// Three read fetches in parallel: the route itself, its matches, and the map
// data. Footer actions: pause/resume (PATCH is_active), edit, delete. Match
// cards link straight into the request composer with the leg dates prefilled.

import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Image,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { EmptyState, MotionSection, PageHero, StatCard } from '../../components/ui';
import { ApiError, fetchOk } from '../../lib/api';
import { csrfHeaders } from '../../lib/auth-context';
import { formatDateRangeMs } from '../../lib/date-format';
import { showToast } from '../../lib/toast';

// Leaflet touches `window` at module-eval time and the map only renders
// after the route + map fetches resolve, so defer it to its own chunk.
const TourRouteMap = lazy(() => import('../../components/tours/TourRouteMap'));

type TourMapCityDTO = {
  name: string;
  displayName: string;
  lat: number | null;
  lng: number | null;
  order: number;
  source: 'venue_centroid' | 'geocode' | 'unknown';
  legFromDate: number | null;
  legToDate: number | null;
  corridorRadiusKm: number;
};

type TourMapDTO = {
  cities: TourMapCityDTO[];
  venuePins: Array<{
    id: string;
    slug: string;
    name: string;
    city: string;
    lat: number;
    lng: number;
  }>;
  openWeekPins: Array<{
    id: string;
    venue: { id: string; slug: string; name: string; lat: number; lng: number };
    fromDate: number;
    toDate: number;
  }>;
  corridorRadiusKm: number;
};

const MAP_FAILED = 'failed' as const;
type MapState = TourMapDTO | typeof MAP_FAILED | null;

export const Route = createFileRoute('/tours/$id')({
  component: TourDetailPage,
});

type TourRouteCityLeg = {
  name: string;
  fromDate?: number;
  toDate?: number;
};

type TourRouteDTO = {
  id: string;
  name: string;
  cities: TourRouteCityLeg[];
  fromDate: number;
  toDate: number;
  isActive: boolean;
  notifyOnMatch: boolean;
  corridorRadiusKm: number;
  createdAt: number;
  updatedAt: number;
};

type VenueMatchDTO = {
  id: string;
  slug: string;
  name: string;
  city: string;
  region: string | null;
  country: string;
  heroImageUrl: string | null;
  matchedCity: string;
};

type OpenWeekMatchDTO = {
  id: string;
  fromDate: number;
  toDate: number;
  notes: string | null;
  venue: { id: string; slug: string; name: string; city: string };
  matchedCity: string;
};

type MatchesDTO = {
  venues: VenueMatchDTO[];
  openWeeks: OpenWeekMatchDTO[];
};

function msToYmd(ms: number): string {
  const d = new Date(ms);
  const yyyy = d.getUTCFullYear().toString().padStart(4, '0');
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const dd = d.getUTCDate().toString().padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function titleCaseCity(s: string): string {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

function TourDetailPage(): React.JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [route, setRoute] = useState<TourRouteDTO | null>(null);
  const [matches, setMatches] = useState<MatchesDTO | null>(null);
  const [mapData, setMapData] = useState<MapState>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const loadAll = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const [routeRes, matchesRes, mapRes] = await Promise.all([
        fetchOk(`/api/me/tour-routes`, { credentials: 'include' }),
        fetchOk(`/api/me/tour-routes/${encodeURIComponent(id)}/matches`, {
          credentials: 'include',
        }),
        // Map fetch is best-effort — a failure here shouldn't fail the page.
        fetchOk(`/api/me/tour-routes/${encodeURIComponent(id)}/map`, {
          credentials: 'include',
        }).catch(() => null),
      ]);
      const routesJson = (await routeRes.json()) as { items: TourRouteDTO[] };
      const matchesJson = (await matchesRes.json()) as MatchesDTO;
      const found = routesJson.items.find((r) => r.id === id) ?? null;
      setRoute(found);
      setMatches(matchesJson);
      if (mapRes === null) {
        setMapData(MAP_FAILED);
      } else {
        try {
          const mapJson = (await mapRes.json()) as TourMapDTO;
          setMapData(mapJson);
        } catch {
          setMapData(MAP_FAILED);
        }
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError("This route isn't here. It may have been removed.");
      } else {
        setError(err instanceof ApiError ? err.message : "We couldn't load this route.");
      }
    }
  }, [id]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // Map of lower-cased leg name -> per-leg window (used by venue-match CTAs).
  const legWindowByName = useMemo(() => {
    const map = new Map<string, { from: number; to: number }>();
    if (route === null) return map;
    for (const leg of route.cities) {
      const from = leg.fromDate ?? route.fromDate;
      const to = leg.toDate ?? route.toDate;
      map.set(leg.name, { from, to });
    }
    return map;
  }, [route]);

  async function toggleActive(): Promise<void> {
    if (route === null) return;
    setBusy(true);
    try {
      const res = await fetchOk(`/api/me/tour-routes/${encodeURIComponent(route.id)}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify({ is_active: !route.isActive }),
      });
      const json = (await res.json()) as { item: TourRouteDTO };
      setRoute(json.item);
      showToast({
        kind: 'success',
        message: json.item.isActive ? 'Route active.' : 'Route paused.',
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        showToast({
          kind: 'warning',
          message: 'This route changed in another tab. Refreshing.',
        });
        await loadAll();
      } else {
        showToast({
          kind: 'error',
          message: err instanceof ApiError ? err.message : "We couldn't update that.",
        });
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (route === null) return;
    setBusy(true);
    try {
      await fetchOk(`/api/me/tour-routes/${encodeURIComponent(route.id)}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: csrfHeaders(),
      });
      showToast({ kind: 'success', message: 'Route removed.' });
      navigate({ to: '/tours' });
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : "We couldn't remove that route.",
      });
      setBusy(false);
    }
  }

  if (error !== null && route === null) {
    return (
      <AppShell tone="content">
        <Stack gap="md" py="xl">
          <Text c="red" role="alert">
            {error}
          </Text>
          <Anchor component={Link} to="/tours">
            Back to routes
          </Anchor>
        </Stack>
      </AppShell>
    );
  }

  if (route === null || matches === null) {
    return (
      <AppShell tone="content">
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </AppShell>
    );
  }

  const dayCount = Math.max(
    1,
    Math.round((route.toDate - route.fromDate) / (1000 * 60 * 60 * 24)) + 1,
  );

  return (
    <AppShell tone="content">
      <Stack gap="lg">
        <PageHero
          eyebrow="Tour routes"
          title={route.name}
          lede={`${route.cities.length} ${route.cities.length === 1 ? 'city' : 'cities'} · ${formatDateRangeMs(route.fromDate, route.toDate)}`}
          action={
            <Group gap="sm" wrap="wrap" justify="flex-end">
              <Anchor component={Link} to="/tours" size="sm">
                Back to routes
              </Anchor>
              <Link
                to="/tours/$id/edit"
                params={{ id: route.id }}
                style={{ textDecoration: 'none' }}
              >
                <Button component="span" tabIndex={-1} variant="light">
                  Edit route
                </Button>
              </Link>
            </Group>
          }
        />

        {mapData !== MAP_FAILED ? (
          <MotionSection>
            <Suspense
              fallback={
                <Box style={{ height: 360 }} ta="center">
                  <Loader />
                </Box>
              }
            >
              {mapData !== null ? (
                <TourRouteMap
                  cities={mapData.cities}
                  venuePins={mapData.venuePins}
                  openWeekPins={mapData.openWeekPins}
                  corridorRadiusKm={mapData.corridorRadiusKm}
                />
              ) : (
                <Box style={{ height: 360 }} ta="center">
                  <Loader />
                </Box>
              )}
            </Suspense>
          </MotionSection>
        ) : null}

        <MotionSection>
          <Group gap="md" wrap="wrap" align="stretch">
            <Box style={{ flex: 1, minWidth: 200 }}>
              <StatCard label="Cities" value={route.cities.length} />
            </Box>
            <Box style={{ flex: 1, minWidth: 200 }}>
              <StatCard label="Day window" value={dayCount} />
            </Box>
            <Box style={{ flex: 1, minWidth: 200 }}>
              <StatCard
                label="Corridor"
                value={
                  route.corridorRadiusKm === 0
                    ? 'Exact city'
                    : `${route.corridorRadiusKm} km`
                }
              />
            </Box>
            <Box style={{ flex: 1, minWidth: 200 }}>
              <StatCard label="Status" value={route.isActive ? 'Active' : 'Paused'} />
            </Box>
          </Group>
        </MotionSection>

        <MotionSection>
          <Stack gap="xs">
            <Text fw={600} size="sm" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.04em' }}>
              Cities in this route
            </Text>
            <Group gap={6} wrap="wrap">
              {route.cities.map((leg, idx) => (
                <Badge key={`${idx}-${leg.name}`} variant="outline" tt="capitalize">
                  {leg.name}
                </Badge>
              ))}
            </Group>
          </Stack>
        </MotionSection>

        <Divider />

        <MotionSection>
          <Stack gap="md">
            <Text fw={600} size="lg">
              New venues in your cities
            </Text>
            {matches.venues.length === 0 ? (
              <EmptyState
                title="No venue matches yet."
                body="When a host publishes a venue in one of your cities, it'll show up here."
              />
            ) : (
              <Stack gap="sm">
                {matches.venues.map((venue) => {
                  const window = legWindowByName.get(venue.matchedCity) ?? {
                    from: route.fromDate,
                    to: route.toDate,
                  };
                  return (
                    <VenueMatchCard
                      key={venue.id}
                      venue={venue}
                      windowFrom={window.from}
                      windowTo={window.to}
                      routeId={route.id}
                      corridorRadiusKm={route.corridorRadiusKm}
                    />
                  );
                })}
              </Stack>
            )}
          </Stack>
        </MotionSection>

        <Divider />

        <MotionSection>
          <Stack gap="md">
            <Text fw={600} size="lg">
              Open weeks in your window
            </Text>
            {matches.openWeeks.length === 0 ? (
              <EmptyState
                title="No open weeks yet."
                body="Hosts can post the windows they'd love to host in. We'll surface anything that overlaps yours."
              />
            ) : (
              <Stack gap="sm">
                {matches.openWeeks.map((week) => (
                  <OpenWeekMatchCard
                    key={week.id}
                    week={week}
                    routeId={route.id}
                    corridorRadiusKm={route.corridorRadiusKm}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </MotionSection>

        <Divider />

        <Paper withBorder p="md" radius="md">
          <Group justify="space-between" wrap="wrap">
            <Stack gap={2}>
              <Text fw={500}>
                {route.isActive ? 'Receiving matches' : 'Paused'}
              </Text>
              <Text size="sm" c="dimmed">
                {route.isActive
                  ? "We'll keep watching while this is active."
                  : "Resume to start watching again."}
              </Text>
            </Stack>
            <Group gap="sm">
              <Link to="/tours/$id/edit" params={{ id: route.id }} style={{ textDecoration: 'none' }}>
                <Button component="span" tabIndex={-1} variant="light">
                  Edit route
                </Button>
              </Link>
              <Button
                variant="light"
                onClick={() => void toggleActive()}
                loading={busy}
              >
                {route.isActive ? 'Pause route' : 'Resume route'}
              </Button>
              <Button color="red" variant="subtle" onClick={() => setConfirmDelete(true)}>
                Remove route
              </Button>
            </Group>
          </Group>
        </Paper>
      </Stack>

      <Modal
        opened={confirmDelete}
        onClose={() => (busy ? undefined : setConfirmDelete(false))}
        title="Remove this route?"
        centered
      >
        <Stack gap="md">
          <Text>
            "{route.name}" will stop sending match notifications. You can save a new route any time.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={() => setConfirmDelete(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button color="red" loading={busy} onClick={() => void handleDelete()}>
              Remove route
            </Button>
          </Group>
        </Stack>
      </Modal>
    </AppShell>
  );
}

function VenueMatchCard({
  venue,
  windowFrom,
  windowTo,
  routeId,
  corridorRadiusKm,
}: {
  venue: VenueMatchDTO;
  windowFrom: number;
  windowTo: number;
  routeId: string;
  corridorRadiusKm: number;
}): React.JSX.Element {
  const place = [venue.city, venue.region, venue.country].filter(Boolean).join(', ');
  const matchedVia =
    venue.matchedCity.toLowerCase() !== venue.city.toLowerCase()
      ? `(matched via ${titleCaseCity(venue.matchedCity)} +${corridorRadiusKm} km)`
      : null;
  const requestSearch = {
    from: msToYmd(windowFrom),
    to: msToYmd(windowTo),
    route: routeId,
    city: venue.matchedCity,
  };
  return (
    <Card withBorder padding="sm" radius="md">
      <Group wrap="nowrap" gap="md" align="stretch">
        <Link
          to="/venues/$slug"
          params={{ slug: venue.slug }}
          style={{ textDecoration: 'none', color: 'inherit', flexShrink: 0 }}
        >
          {venue.heroImageUrl !== null ? (
            <Image
              src={venue.heroImageUrl}
              alt={venue.name}
              w={96}
              h={72}
              fit="cover"
              radius="sm"
            />
          ) : (
            <Box
              w={96}
              h={72}
              style={{
                background:
                  'linear-gradient(135deg, var(--mantine-color-brand-1), var(--mantine-color-sun-1))',
                borderRadius: 6,
              }}
            />
          )}
        </Link>
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Link
            to="/venues/$slug"
            params={{ slug: venue.slug }}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Text fw={600}>{venue.name}</Text>
            <Text size="sm" c="dimmed">
              {place}
            </Text>
          </Link>
          {matchedVia !== null ? (
            <Text size="xs" c="dimmed">
              {matchedVia}
            </Text>
          ) : null}
        </Stack>
        <Box style={{ alignSelf: 'center' }}>
          <Link
            to="/request/$venueId"
            params={{ venueId: venue.id }}
            search={requestSearch}
            style={{ textDecoration: 'none' }}
          >
            <Button
              component="span"
              tabIndex={-1}
              variant="gradient"
              gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
            >
              Send a request
            </Button>
          </Link>
        </Box>
      </Group>
    </Card>
  );
}

function OpenWeekMatchCard({
  week,
  routeId,
  corridorRadiusKm,
}: {
  week: OpenWeekMatchDTO;
  routeId: string;
  corridorRadiusKm: number;
}): React.JSX.Element {
  const matchedVia =
    week.matchedCity.toLowerCase() !== week.venue.city.toLowerCase()
      ? `(matched via ${titleCaseCity(week.matchedCity)} +${corridorRadiusKm} km)`
      : null;
  const requestSearch = {
    from: msToYmd(week.fromDate),
    to: msToYmd(week.toDate),
    route: routeId,
    city: week.matchedCity,
  };
  return (
    <Card withBorder padding="sm" radius="md">
      <Stack gap="xs">
        <Group justify="space-between" wrap="wrap" align="flex-start">
          <Link
            to="/venues/$slug"
            params={{ slug: week.venue.slug }}
            style={{ textDecoration: 'none', color: 'inherit', flex: 1, minWidth: 0 }}
          >
            <Stack gap={2}>
              <Text fw={600}>{week.venue.name}</Text>
              <Text size="sm" c="dimmed">
                {week.venue.city}
              </Text>
              {matchedVia !== null ? (
                <Text size="xs" c="dimmed">
                  {matchedVia}
                </Text>
              ) : null}
            </Stack>
          </Link>
          <Group gap="sm" wrap="nowrap" align="center">
            <Badge variant="light" color="brand">
              {formatDateRangeMs(week.fromDate, week.toDate)}
            </Badge>
            <Link
              to="/request/$venueId"
              params={{ venueId: week.venue.id }}
              search={requestSearch}
              style={{ textDecoration: 'none' }}
            >
              <Button
                component="span"
                tabIndex={-1}
                size="sm"
                variant="gradient"
                gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
              >
                Send a request
              </Button>
            </Link>
          </Group>
        </Group>
        {week.notes !== null && week.notes.length > 0 ? (
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {week.notes}
          </Text>
        ) : null}
      </Stack>
    </Card>
  );
}
